import { useMemo } from "react";
import { api } from "../api";
import { clearCache } from "../cache";
import { go, useStore } from "../store";
import { Avatar, isOwner, roomColor, roomTitle, useMe } from "./ui";
import { Profile } from "./Profile";
import { useState } from "react";
import { Icon, Plus, type IconName } from "./icons";

export function Sidebar({ onNew, onNewGroup, onPeople }: { onNew: () => void; onNewGroup: () => void; onPeople: () => void }) {
  const me = useMe();
  const { users, rooms, states, tasks, route, online, follows, saved } = useStore();
  const mentionCount = Object.values(states).reduce((a, s) => a + (rooms[s.roomId] ? s.mentions : 0), 0);
  const threadCount = Object.values(follows).filter((f) => f.lastReplyId > f.lastReadId).length;
  const dueCount = Object.values(saved).filter((s) => s.remindAt && s.reminded).length;
  const unreadChats = Object.values(states).filter((s) => rooms[s.roomId] && s.unread > 0 && s.notify !== "none").length;
  const open = Object.values(tasks).filter((t) => t.ownerId === me.id && t.status !== "done").length;

  const { groups, dms } = useMemo(() => {
    const all = Object.values(rooms);
    const groups = all.filter((r) => r.kind === "group").sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    const dms = all.filter((r) => r.kind === "dm").sort((a, b) => roomTitle(a, me.id, users).localeCompare(roomTitle(b, me.id, users)));
    return { groups, dms };
  }, [rooms, users, me.id]);

  const [prof, setProf] = useState(false);
  const item = (id: number, label: React.ReactNode, lead: React.ReactNode, color: string) => {
    const st = states[id];
    const on = route.v === "room" && route.id === id;
    const unread = st?.unread ?? 0, level = st?.notify ?? "all", men = st?.mentions ?? 0;
    // Muted: dim, never bold. Mentions-only: bold, no count. @mentions always show a count.
    const bold = unread > 0 && level !== "none";
    const count = men > 0 ? men : level === "all" ? unread : 0;
    return (
      <button key={id} className={`it${on ? " on" : ""}${bold ? " new" : ""}${level === "none" ? " muted" : ""}`} style={{ ["--c" as any]: color }} onClick={() => go({ v: "room", id })}>
        {lead}<span className="t">{label}</span>{level === "none" && <span className="mute"><Icon n="belloff" s={13} /></span>}{count > 0 && <span className={`badge${men ? " men" : ""}`}>{count > 99 ? "99+" : count}</span>}
      </button>
    );
  };

  const nav = (v: "mentions" | "threads" | "later" | "unreads", icon: IconName, label: string, n: number, hot = false) => (
    <button className={`it${route.v === v ? " on" : ""}${n ? " new" : ""}`} style={{ ["--c" as any]: "#8a94a6" }} onClick={() => go({ v })}>
      <Icon n={icon} /><span className="t">{label}</span>{n > 0 && <span className={`badge ${hot ? "men" : "cnt"}`}>{n}</span>}
    </button>
  );

  const signOut = async () => { await api.logout().catch(() => {}); await clearCache(); location.href = "/"; };

  return (
    <aside className="side">
      <div className="logo"><span className="mk">R</span>Rockenue{!online && <span className="off">reconnecting…</span>}</div>
      <button className="newc" onClick={onNew}><b><Plus s={12} /></b>New message<kbd>⌘K</kbd></button>
      <div className="grp">
        <button className={`it${route.v === "tasks" || route.v === "task" ? " on" : ""}`} style={{ ["--c" as any]: "#8a94a6" }} onClick={() => go({ v: "tasks", tab: isOwner(me) ? "everyone" : "mine" })}>
          <Icon n="tasks" /><span className="t">Tasks</span>{open > 0 && <span className="badge cnt">{open}</span>}
        </button>
        {nav("mentions", "at", "Mentions", mentionCount, true)}
        {nav("threads", "threads", "Threads", threadCount)}
        {nav("later", "later", "Later", dueCount)}
        {unreadChats > 0 && nav("unreads", "unreads", "All unreads", unreadChats)}
      </div>
      <h6>Groups{isOwner(me) && <button title="New group" onClick={onNewGroup}><Plus s={12} /></button>}</h6>
      <div className="grp">
        {groups.map((r) => item(r.id, r.name ?? "", <span className="gh">#</span>, roomColor(r, me.id, users)))}
        {!groups.length && <span className="nothing" style={{ padding: "2px 0" }}>No groups yet</span>}
      </div>
      <h6>Direct messages<button title="New message" onClick={onNew}><Plus s={12} /></button></h6>
      <div className="grp">
        {dms.map((r) => {
          const others = r.members.filter((u) => u !== me.id).map((u) => users[u]);
          if (others.length > 1) {
            const lead = <span className="stack"><Avatar user={others[0]} size={14} /><Avatar user={others[1]} size={14} /></span>;
            return item(r.id, <span className="dmn"><span>{others.map((o) => o?.name).join(", ")}</span></span>, lead, "#8a94a6");
          }
          const other = others[0] ?? me;
          const label = <span className="dmn"><span>{other?.name ?? ""}</span>{other?.status && <small>{other.status}</small>}</span>;
          return item(r.id, label, <Avatar user={other} size={20} dot />, other?.color ?? "#888");
        })}
      </div>
      <div className="meline">
        <button className="mebtn" onClick={() => setProf((p) => !p)}>
          <Avatar user={me} size={28} dot /><span>{me.name}<small>{me.status || "Set a status"}</small></span>
        </button>
        {isOwner(me) && <span className="acts"><button onClick={onPeople}>People</button></span>}
      </div>
      {prof && <Profile onClose={() => setProf(false)} onSignOut={signOut} />}
    </aside>
  );
}
